import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtDate, brl } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Users, Briefcase, Tag, BedDouble, Building2, Trees } from "lucide-react";
import { toast } from "sonner";

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Deriva a situação real de um quarto usando as reservas relacionadas + status do quarto.
// Regras da FASE 3C: pre-reserva NÃO conta como ocupado; hospedado (ou "ativa" legado)
// só conta se hoje está dentro do período. Compatível com reservas antigas.
// FASE 3F: exibe também os estados de limpeza (aguardando_limpeza, em_limpeza, aguardando_liberacao).
function computeSituacao(quarto, reservasDoQuarto, hoje) {
  const hospedadoAgora = reservasDoQuarto.find(
    (r) =>
      (r.status === "hospedado" || r.status === "ativa") &&
      r.data_entrada <= hoje &&
      r.data_saida > hoje,
  );
  if (hospedadoAgora) return { tipo: "hospedado", reserva: hospedadoAgora };

  // Status físico do quarto tem prioridade sobre próximas reservas
  if (quarto.status === "aguardando_limpeza") return { tipo: "aguardando_limpeza", reserva: null };
  if (quarto.status === "em_limpeza") return { tipo: "em_limpeza", reserva: null };
  if (quarto.status === "aguardando_liberacao") return { tipo: "aguardando_liberacao", reserva: null };
  if (quarto.status === "manutencao") return { tipo: "manutencao", reserva: null };

  const proximaPre = reservasDoQuarto
    .filter(
      (r) =>
        (r.status === "pre-reserva" || r.status === "ativa") && r.data_entrada > hoje,
    )
    .sort((a, b) => a.data_entrada.localeCompare(b.data_entrada))[0];
  if (proximaPre) return { tipo: "pre-reserva", reserva: proximaPre };

  return { tipo: "livre", reserva: null };
}

// FASE 3G+: uma reserva de Chácara Inteira só "bloqueia suítes" quando é 2+ diárias
// (pernoite obrigatório) OU 1 diária com bloqueia_suites_dia_unico=true.
function ciBloqueiaSuites(r) {
  if (Number(r?.diarias || 0) >= 2) return true;
  return !!r?.bloqueia_suites_dia_unico;
}

// FASE 3G: verifica se a chácara inteira desta pousada está reservada agora
// e devolve a reserva ativa (para exibir aviso e marcar quartos individuais como bloqueados)
// Só devolve quando a reserva REALMENTE bloqueia as suítes.
function findChacaraInteiraReservaAtiva(quartos, reservas, hoje) {
  const chacaraInteira = quartos.find((q) => q.is_chacara_inteira);
  if (!chacaraInteira) return null;
  return reservas.find((r) =>
    r.quarto_id === chacaraInteira.id &&
    (r.status === "pre-reserva" || r.status === "hospedado" || r.status === "ativa") &&
    r.data_entrada <= hoje && r.data_saida > hoje &&
    ciBloqueiaSuites(r),
  ) || null;
}

const SITUACAO_LABEL = {
  hospedado: "Hospedado",
  "pre-reserva": "Pré-reserva",
  aguardando_limpeza: "Aguardando limpeza",
  em_limpeza: "Em limpeza",
  aguardando_liberacao: "Aguardando liberação",
  manutencao: "Manutenção",
  bloqueado_chacara: "Bloqueado (chácara inteira)",
  livre: "Livre",
};

const SITUACAO_STYLE = {
  hospedado: { bg: "bg-[#EAF0EA]", text: "text-[#083A6B]", dot: "bg-[#083A6B]" },
  "pre-reserva": { bg: "bg-[#FDF3E3]", text: "text-[#8B6A1F]", dot: "bg-[#8B6A1F]" },
  aguardando_limpeza: { bg: "bg-[#FEE7DE]", text: "text-[#B4472B]", dot: "bg-[#B4472B]" },
  em_limpeza: { bg: "bg-[#DBE7F2]", text: "text-[#1E4C7A]", dot: "bg-[#3671B0]" },
  aguardando_liberacao: { bg: "bg-[#E9DFEF]", text: "text-[#5E3B7A]", dot: "bg-[#7B4FA1]" },
  manutencao: { bg: "bg-[#FBE7DE]", text: "text-[#D4A652]", dot: "bg-[#D4A652]" },
  bloqueado_chacara: { bg: "bg-stone-200", text: "text-stone-700", dot: "bg-stone-500" },
  livre: { bg: "bg-stone-100", text: "text-stone-600", dot: "bg-stone-300" },
};

export default function PousadaOcupacao() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pousada, setPousada] = useState(null);
  const [quartos, setQuartos] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detalhe, setDetalhe] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: pousadas }, { data: q }, { data: r }] = await Promise.all([
          api.get("/pousadas"),
          api.get("/quartos"),
          api.get("/reservas"),
        ]);
        const p = pousadas.find((x) => x.id === id);
        if (!p) {
          toast.error("Pousada não encontrada");
          navigate("/admin/pousadas", { replace: true });
          return;
        }
        setPousada(p);
        setQuartos(q.filter((x) => x.pousada_id === id).sort((a, b) => String(a.numero).localeCompare(String(b.numero), "pt-BR", { numeric: true })));
        setReservas(r.filter((x) => x.pousada_id === id));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Falha ao carregar ocupação. Tente novamente.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const hoje = todayISO();

  const chacaraInteiraAtiva = useMemo(
    () => (pousada?.is_chacara ? findChacaraInteiraReservaAtiva(quartos, reservas, hoje) : null),
    [pousada?.is_chacara, quartos, reservas, hoje],
  );

  const quartosComSituacao = useMemo(
    () => quartos.map((q) => {
      const situacao = computeSituacao(q, reservas.filter((r) => r.quarto_id === q.id), hoje);
      // FASE 3G: se a chácara inteira está reservada agora, quartos individuais livres ficam "bloqueado_chacara"
      if (
        chacaraInteiraAtiva &&
        !q.is_chacara_inteira &&
        situacao.tipo === "livre"
      ) {
        return { quarto: q, tipo: "bloqueado_chacara", reserva: chacaraInteiraAtiva };
      }
      return { quarto: q, ...situacao };
    }),
    [quartos, reservas, hoje, chacaraInteiraAtiva],
  );

  const resumo = useMemo(() => {
    let hospedado = 0, preReserva = 0, livre = 0, manut = 0, limpeza = 0;
    for (const it of quartosComSituacao) {
      if (it.tipo === "hospedado") hospedado++;
      else if (it.tipo === "pre-reserva") preReserva++;
      else if (it.tipo === "manutencao") manut++;
      else if (it.tipo === "aguardando_limpeza" || it.tipo === "em_limpeza" || it.tipo === "aguardando_liberacao") limpeza++;
      else livre++;
    }
    return { total: quartosComSituacao.length, hospedado, preReserva, livre, manut, limpeza };
  }, [quartosComSituacao]);

  if (loading) {
    return <div data-testid="ocupacao-loading" className="text-stone-500 text-sm">Carregando ocupação…</div>;
  }
  if (!pousada) return null;

  return (
    <div data-testid="pousada-ocupacao-page">
      <div className="mb-6">
        <Link to="/admin/pousadas" data-testid="btn-voltar-pousadas" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#083A6B]">
          <ArrowLeft size={16} /> Voltar para pousadas
        </Link>
      </div>

      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4A652] mb-3 font-semibold">Ocupação em tempo real</div>
        <h1 className="font-serif text-4xl text-stone-900 leading-tight mb-2 flex items-center gap-3">
          {pousada.nome}
          {pousada.is_chacara && (
            <span
              data-testid="badge-pousada-chacara"
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest bg-[#EAF0EA] text-[#083A6B] border border-[#083A6B]/20 px-2 py-1 rounded-full font-sans font-medium"
            >
              <Trees size={12} /> Chácara
            </span>
          )}
        </h1>
        {pousada.endereco && <div className="text-sm text-stone-500">{pousada.endereco}</div>}
      </div>

      {chacaraInteiraAtiva && (
        <div
          data-testid="chacara-inteira-ativa-banner"
          className="mb-6 bg-[#EAF0EA] border border-[#083A6B]/30 rounded-lg p-4 flex items-start gap-3"
        >
          <Trees size={18} className="text-[#083A6B] mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-semibold text-[#083A6B]">
              Chácara inteira reservada de {fmtDate(chacaraInteiraAtiva.data_entrada)} até {fmtDate(chacaraInteiraAtiva.data_saida)}
              {" "}({(chacaraInteiraAtiva.diarias || 0) >= 2 ? "com pernoite" : "diária avulsa com bloqueio de suítes"})
            </div>
            <div className="text-xs text-stone-600 mt-0.5">
              Todos os quartos individuais estão bloqueados neste período — sem possibilidade de venda individual.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
        <div className="bg-[#FFFFFF] border border-stone-200 rounded-md p-5">
          <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Total de quartos</div>
          <div className="font-serif text-3xl text-stone-900" data-testid="resumo-total">{resumo.total}</div>
        </div>
        <div className="bg-[#EAF0EA] border border-[#083A6B]/20 rounded-md p-5">
          <div className="text-[10px] uppercase tracking-widest text-[#083A6B] mb-1">Hospedados</div>
          <div className="font-serif text-3xl text-[#083A6B]" data-testid="resumo-hospedados">{resumo.hospedado}</div>
        </div>
        <div className="bg-[#FDF3E3] border border-[#8B6A1F]/20 rounded-md p-5">
          <div className="text-[10px] uppercase tracking-widest text-[#8B6A1F] mb-1">Pré-reserva</div>
          <div className="font-serif text-3xl text-[#8B6A1F]" data-testid="resumo-prereserva">{resumo.preReserva}</div>
        </div>
        <div className="bg-[#FEE7DE] border border-[#B4472B]/20 rounded-md p-5">
          <div className="text-[10px] uppercase tracking-widest text-[#B4472B] mb-1">Em limpeza</div>
          <div className="font-serif text-3xl text-[#B4472B]" data-testid="resumo-limpeza">{resumo.limpeza}</div>
        </div>
        <div className="bg-white border border-stone-200 rounded-md p-5">
          <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Livres</div>
          <div className="font-serif text-3xl text-stone-700" data-testid="resumo-livres">{resumo.livre}</div>
        </div>
        <div className="bg-[#FBEDD1] border border-[#8B6A1F]/20 rounded-md p-5">
          <div className="text-[10px] uppercase tracking-widest text-[#8B6A1F] mb-1">Manutenção</div>
          <div className="font-serif text-3xl text-[#8B6A1F]" data-testid="resumo-manutencao">{resumo.manut}</div>
        </div>
      </div>

      {quartosComSituacao.length === 0 ? (
        <div className="bg-[#FFFFFF] border border-dashed border-stone-300 rounded-md p-12 text-center">
          <BedDouble size={40} className="mx-auto text-stone-300 mb-4" strokeWidth={1.4} />
          <div className="text-stone-500 text-sm">Nenhum quarto cadastrado nesta pousada.</div>
          <Link to="/admin/quartos" className="inline-block mt-4 text-sm text-[#083A6B] underline">Cadastrar quartos</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {quartosComSituacao.map(({ quarto, tipo, reserva }) => {
            const style = SITUACAO_STYLE[tipo] || SITUACAO_STYLE.livre;
            const label = SITUACAO_LABEL[tipo] || "Livre";
            const clickable = !!reserva && tipo !== "bloqueado_chacara";
            const isChacaraInteira = !!quarto.is_chacara_inteira;
            const emptyMsg =
              tipo === "manutencao" ? "Quarto em manutenção." :
              tipo === "aguardando_limpeza" ? "Aguardando início da limpeza." :
              tipo === "em_limpeza" ? "Limpeza em andamento." :
              tipo === "aguardando_liberacao" ? "Aguardando liberação para novos hóspedes." :
              tipo === "bloqueado_chacara" ? "Bloqueado pela reserva da chácara inteira no período." :
              "Disponível para reserva.";
            return (
              <div
                key={quarto.id}
                data-testid={`quarto-card-${quarto.id}`}
                data-situacao={tipo}
                onClick={() => clickable && setDetalhe(reserva)}
                className={`bg-[#FFFFFF] border rounded-lg p-5 transition-all ${isChacaraInteira ? "border-[#083A6B]/40 ring-1 ring-[#083A6B]/10" : "border-stone-200"} ${clickable ? "cursor-pointer hover:border-[#083A6B] hover:shadow-sm" : ""}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-serif text-2xl text-stone-900 leading-none flex items-center gap-2">
                      {isChacaraInteira ? (
                        <><Trees size={20} className="text-[#083A6B]" /> Chácara Inteira</>
                      ) : (
                        <>Quarto {quarto.numero}</>
                      )}
                    </div>
                    <div className="text-xs text-stone-500 mt-1 capitalize">{quarto.tipo || "—"} · até {quarto.capacidade || 1} pax</div>
                  </div>
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                    {label}
                  </div>
                </div>

                {reserva && tipo !== "bloqueado_chacara" ? (
                  <div className="pt-3 border-t border-stone-200 space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-stone-800 font-medium">
                      <Users size={12} /> {reserva.hospede_nome}
                    </div>
                    {reserva.empresa_nome && (
                      <div className="flex items-center gap-2 text-[#D4A652] text-xs">
                        <Briefcase size={12} /> {reserva.empresa_nome}
                      </div>
                    )}
                    <div className="text-xs text-stone-500">
                      {fmtDate(reserva.data_entrada)} → {fmtDate(reserva.data_saida)} · {reserva.diarias || 0} diárias · {reserva.num_pessoas || 1} pax
                    </div>
                  </div>
                ) : (
                  <div className="pt-3 border-t border-stone-200 text-xs text-stone-400">
                    Sem hóspede atualmente. {emptyMsg}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!detalhe} onOpenChange={(v) => !v && setDetalhe(null)}>
        <DialogContent data-testid="dialog-detalhes-quarto" className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-3">
              Reserva do quarto
              {detalhe && <StatusPill status={detalhe.status} testid="detalhe-quarto-status" />}
            </DialogTitle>
          </DialogHeader>
          {detalhe && (
            <div className="space-y-5 text-sm">
              <section>
                <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">Hóspede</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="text-stone-500 text-xs">Nome</div><div className="text-stone-800 font-medium">{detalhe.hospede_nome || "—"}</div></div>
                  <div><div className="text-stone-500 text-xs">Telefone</div><div className="text-stone-800">{detalhe.hospede_telefone || "—"}</div></div>
                  <div><div className="text-stone-500 text-xs">Documento</div><div className="text-stone-800">{detalhe.hospede_documento || "—"}</div></div>
                  {detalhe.empresa_nome && (
                    <div><div className="text-stone-500 text-xs">Empresa</div><div className="text-[#D4A652] font-medium flex items-center gap-1"><Briefcase size={12} />{detalhe.empresa_nome}</div></div>
                  )}
                </div>
              </section>
              <section>
                <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">Estadia</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="text-stone-500 text-xs">Quarto</div><div className="text-stone-800 font-medium">Nº {detalhe.quarto_numero}</div></div>
                  <div><div className="text-stone-500 text-xs">Tipo</div><div className="text-stone-800 capitalize">{detalhe.quarto_tipo || "—"}</div></div>
                  <div><div className="text-stone-500 text-xs">Entrada</div><div className="text-stone-800">{fmtDate(detalhe.data_entrada)}</div></div>
                  <div><div className="text-stone-500 text-xs">Saída</div><div className="text-stone-800">{fmtDate(detalhe.data_saida)}</div></div>
                  <div><div className="text-stone-500 text-xs">Diárias</div><div className="text-stone-800">{detalhe.diarias}</div></div>
                  <div><div className="text-stone-500 text-xs">Pessoas</div><div className="text-stone-800 flex items-center gap-1"><Users size={12} />{detalhe.num_pessoas || 1}</div></div>
                </div>
              </section>
              <section className="bg-stone-50 border border-stone-200 rounded-md p-4">
                <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">Financeiro</div>
                <div className="grid grid-cols-3 gap-3">
                  <div><div className="text-stone-500 text-xs">Diária</div><div className="text-stone-800 font-medium">{brl(detalhe.valor_diaria)}</div></div>
                  <div><div className="text-stone-500 text-xs">Total</div><div className="font-serif text-lg text-[#083A6B]">{brl(detalhe.valor_total)}</div></div>
                  <div><div className="text-stone-500 text-xs">Pago</div><div className="text-stone-800">{brl(detalhe.valor_pago)}</div></div>
                </div>
                {detalhe.tarifa_nome && (
                  <div className="mt-3 text-xs text-stone-600 flex items-center gap-1"><Tag size={12} /> Tarifa: {detalhe.tarifa_nome}</div>
                )}
              </section>
              {detalhe.observacoes && (
                <section>
                  <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">Observações</div>
                  <div className="text-stone-700 whitespace-pre-wrap">{detalhe.observacoes}</div>
                </section>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-2">
            <Button data-testid="btn-ir-reserva" variant="outline" onClick={() => navigate("/admin/reservas")}>
              Ir para Reservas
            </Button>
            <Button data-testid="btn-fechar-quarto" className="bg-[#083A6B] hover:bg-[#052B50] text-white" onClick={() => setDetalhe(null)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
